export const GOOGLE_PLAY_MESSAGE_KEYS = [
  "loadingTracks", "noTracks", "tracksHelp", "reloadTracks", "chooseTrack", "existingPackage", "manualPackage", "parsed", "declared", "channel", "googleMethod", "packageMissing", "authorize", "authorizedAccount", "oauthUnavailable", "oauthHelp", "oauthFailed", "oauthCancelled",
  "oauthErrMissingCode", "oauthErrServerConfig", "oauthErrTokenExchange", "oauthErrOfflineAccess", "oauthErrGooglePermissions", "oauthErrAccountIdentity", "oauthErrPlayPermissions", "oauthErrAccessChanged", "oauthErrConnectionChanged", "oauthErrCredentialStorage", "disconnectHelp", "title", "description", "configured", "enabled", "disabled", "verified", "stale", "serviceAccount",
  "packageName", "internalTrack", "closedTrack", "productionTrack", "credentialJson", "chooseFile",
  "saveEnable", "replace", "cancel", "test", "testing", "enable", "disable", "unbind",
  "connect", "connecting", "connected", "retry", "needsConfig", "needsConfigHelp", "expand", "collapse", "saveSuccess", "verifySuccess", "enableSuccess", "disableSuccess", "unbindSuccess", "actionFailed",
  "confirmDisable", "confirmUnbind", "noPublish", "formHelp", "invalidJson",
] as const;

export type GooglePlayMessageKey = typeof GOOGLE_PLAY_MESSAGE_KEYS[number];

export const GOOGLE_PLAY_MESSAGES: Record<"en" | "zh-CN", Record<GooglePlayMessageKey, string>> = {
  en: {
    loadingTracks: "Reading tracks from Google Play…", noTracks: "No tracks were returned for this Play app.", tracksHelp: "Choose from this app’s actual Google Play tracks.", reloadTracks: "Refresh tracks", chooseTrack: "Choose a track",
    existingPackage: "Choose an existing Android package", manualPackage: "Enter another package name", parsed: "From APK inspection", declared: "From upload details", channel: "From channel settings",
    googleMethod: "Google account",
    packageMissing: "Enter the package name of this app in Play Console. No APK or credential file is needed for Google authorization.",
    authorize: "Authorize with Google",
    authorizedAccount: "Google account",
    oauthUnavailable: "Google authorization is not configured on this server.",
    oauthHelp: "Sign in with a Google account that has Play access to this package and the configured tracks. No service-account key is needed.",
    oauthFailed: "Google authorization failed. Try again; if it keeps failing, contact your administrator.",
    oauthCancelled: "Google authorization was cancelled.",
    oauthErrMissingCode: "Google sign-in didn't return an authorization code. Start the connection again.",
    oauthErrServerConfig: "Google authorization isn't fully configured on this server. Contact an administrator.",
    oauthErrTokenExchange: "Couldn't complete the token exchange with Google. Try again in a moment.",
    oauthErrOfflineAccess: "Google didn't grant offline access, so the connection can't be kept. Reconnect and approve access.",
    oauthErrGooglePermissions: "The Google account wasn't granted permission to publish. Grant publishing access and reconnect.",
    oauthErrAccountIdentity: "Couldn't read the connected Google account. Try again.",
    oauthErrPlayPermissions: "Play app verification failed. Check the package name, tracks, and Play permissions, then retry.",
    oauthErrAccessChanged: "Access changed since the connection was made. Reconnect to refresh it.",
    oauthErrConnectionChanged: "The connection changed in Hands during authorization. Reconnect to restore it.",
    oauthErrCredentialStorage: "Authorization succeeded, but saving the credential failed. Try again.",
    disconnectHelp: "Unbind deletes the credential stored by Hands. To revoke Google's grant as well, remove Hands in your Google account connections.",

    title: "Google Play",
    description: "Connect your Google account to this app’s Play Console entry.",
    configured: "Configured", enabled: "Enabled", disabled: "Disabled", verified: "Verified", stale: "Needs verification",
    serviceAccount: "Service account", packageName: "Android package name", internalTrack: "Internal track",
    closedTrack: "Closed testing track", productionTrack: "Production track", credentialJson: "Service account JSON",
    chooseFile: "Choose JSON file", saveEnable: "Validate, save & enable", replace: "Replace credential", cancel: "Cancel",
    connect: "Connect", connecting: "Connecting to Google…", connected: "Connected", retry: "Retry", needsConfig: "Needs configuration", needsConfigHelp: "The Google account is connected. Choose the app package and its Google Play tracks, then save to finish configuring.", expand: "Expand", collapse: "Collapse", test: "Test connection", testing: "Testing…", enable: "Enable", disable: "Disable", unbind: "Unbind",
    saveSuccess: "Google Play binding saved", verifySuccess: "Google Play connection verified",
    enableSuccess: "Google Play enabled", disableSuccess: "Google Play disabled", unbindSuccess: "Google Play unbound",
    actionFailed: "Google Play action failed", confirmDisable: "Disable Google Play promotion for this app?",
    confirmUnbind: "Remove this app's encrypted Google Play credential and binding?",
    noPublish: "Connection testing creates and deletes a temporary Play edit; it never publishes a release.",
    formHelp: "Use a service account that can access this exact package and all three configured tracks.",
    invalidJson: "Choose the complete service-account JSON file downloaded from Google Cloud.",
  },
  "zh-CN": {
    loadingTracks: "正在读取 Google Play 轨道…", noTracks: "此 Play 应用没有返回可用轨道。", tracksHelp: "从此应用的真实 Google Play 轨道中选择。", reloadTracks: "刷新轨道", chooseTrack: "选择轨道",
    existingPackage: "选择已有 Android 包名", manualPackage: "手动填写其他包名", parsed: "APK 解析结果", declared: "上传时声明", channel: "渠道设置",
    googleMethod: "Google 账号授权",
    packageMissing: "请填写此应用在 Play Console 的包名。Google 授权无需选择 APK 或凭据文件。",
    authorize: "使用 Google 授权",
    authorizedAccount: "Google 账号",
    oauthUnavailable: "此服务器尚未配置 Google 授权。",
    oauthHelp: "请登录拥有此包名及所配轨道 Play 权限的 Google 账号，无需创建服务账号密钥。",
    oauthFailed: "Google 授权失败。请重试；若持续失败，请联系管理员。",
    oauthCancelled: "Google 授权已取消。",
    oauthErrMissingCode: "Google 授权未返回授权码（回调不完整）。请重新发起连接。",
    oauthErrServerConfig: "服务端尚未完成 Google 授权配置，请联系管理员。",
    oauthErrTokenExchange: "与 Google 换取令牌失败，请稍后重试。",
    oauthErrOfflineAccess: "未获得离线授权，连接无法保持。请重新连接并同意授权。",
    oauthErrGooglePermissions: "Google 授权未包含发布权限。请授予发布权限后重新连接。",
    oauthErrAccountIdentity: "读取 Google 账号信息失败，请重试。",
    oauthErrPlayPermissions: "Play 应用验证失败，请检查包名、轨道和 Play 权限后重试。",
    oauthErrAccessChanged: "连接后账号权限发生了变化。请重新连接以刷新。",
    oauthErrConnectionChanged: "Hands 中的连接已变更，请重新连接。",
    oauthErrCredentialStorage: "授权成功但凭据保存失败，请重试。",
    disconnectHelp: "解除绑定会删除 Hands 保存的凭据。若也要撤销 Google 的授权，请在 Google 账号的第三方连接中移除 Hands。",

    title: "Google Play",
    description: "授权 Google 账号，将此应用连接到对应的 Play Console 应用。",
    configured: "已配置", enabled: "已启用", disabled: "已停用", verified: "已验证", stale: "需要重新验证",
    serviceAccount: "服务账号", packageName: "Android 包名", internalTrack: "内部测试轨道",
    closedTrack: "封闭测试轨道", productionTrack: "正式发布轨道", credentialJson: "服务账号 JSON",
    chooseFile: "选择 JSON 文件", saveEnable: "验证、保存并启用", replace: "更换凭据", cancel: "取消",
    connect: "连接", connecting: "正在前往 Google 授权…", connected: "已连接", retry: "重试", needsConfig: "待配置", needsConfigHelp: "授权已完成，选择应用包名和 Google Play 返回的轨道后保存即可完成配置。", expand: "展开", collapse: "收起", test: "测试连接", testing: "测试中…", enable: "启用", disable: "停用", unbind: "解除绑定",
    saveSuccess: "Google Play 绑定已保存", verifySuccess: "Google Play 连接验证成功",
    enableSuccess: "Google Play 已启用", disableSuccess: "Google Play 已停用", unbindSuccess: "Google Play 已解除绑定",
    actionFailed: "Google Play 操作失败", confirmDisable: "停用此应用的 Google Play 发布功能？",
    confirmUnbind: "删除此应用加密保存的 Google Play 凭据并解除绑定？",
    noPublish: "连接测试只会创建并删除一个临时 Play edit，不会发布任何版本。",
    formHelp: "请使用能访问这个确切包名和下列三个轨道的服务账号。",
    invalidJson: "请选择从 Google Cloud 下载的完整服务账号 JSON 文件。",
  },
};

// Fixed error contract from the worker side (Volta, #proj-hands b529da37):
// the callback only ever carries these values; anything else falls back to
// the generic copy and the raw code is never rendered.
const OAUTH_FAILURE_MESSAGE_KEYS: Record<string, GooglePlayMessageKey> = {
  missing_code: "oauthErrMissingCode",
  server_configuration: "oauthErrServerConfig",
  token_exchange: "oauthErrTokenExchange",
  offline_access: "oauthErrOfflineAccess",
  google_permissions: "oauthErrGooglePermissions",
  account_identity: "oauthErrAccountIdentity",
  play_permissions: "oauthErrPlayPermissions",
  access_changed: "oauthErrAccessChanged",
  connection_changed: "oauthErrConnectionChanged",
  credential_storage: "oauthErrCredentialStorage",
};

export function googlePlayOAuthFailureMessage(
  errorCode: string | null | undefined,
  languages?: readonly string[],
) {
  // Object.hasOwn: a plain-object lookup would otherwise resolve inherited
  // members for inputs like "__proto__" or "toString" and skip the fallback.
  const key = errorCode && Object.hasOwn(OAUTH_FAILURE_MESSAGE_KEYS, errorCode)
    ? OAUTH_FAILURE_MESSAGE_KEYS[errorCode]
    : undefined;
  return key ? googlePlayMessage(key, languages) : googlePlayMessage("oauthFailed", languages);
}

export function googlePlayMessage(
  key: GooglePlayMessageKey,
  languages: readonly string[] = typeof navigator === "undefined" ? ["en"] : navigator.languages,
) {
  const locale = languages.some((language) => language.toLowerCase().startsWith("zh")) ? "zh-CN" : "en";
  return GOOGLE_PLAY_MESSAGES[locale][key] ?? GOOGLE_PLAY_MESSAGES.en[key];
}
